import { FC, useContext, useMemo } from "react";
import cn from "classnames";
import { iReportData, ReportsNameEnum } from "@models/ChainDetails";
import {
    IconCheckboxFake,
    IconLock,
} from "@icons";
import { getReportDescriptionByPermission } from "../ReportsDownload.helpers";
import "./ReportsItem.styles.scss";
import { isAvailable, PermissionLevelEnum } from "@models/PermissionSection";
import { ChainDetailsDataContext } from "@pages/ChainPage/ChainPage.helpers";
import { AppId, ProductLogo } from "@datassential/platform-ui-lib";

interface iProps {
    data: iReportData;
    onClick: (reportName: ReportsNameEnum) => void;
    isSelected?: boolean;
}

const ReportsItem: FC<iProps> = ({
    data,
    onClick,
    isSelected,
}) => {
    const { data: { mainData: { name: chainName } } } = useContext(ChainDetailsDataContext);

    const {
        data: { name },
        permissionLevel,
    } = data;

    const description = useMemo(() => getReportDescriptionByPermission({
        chainName: chainName,
        permissionLevel,
        reportName: name,
    }), [name, permissionLevel, chainName]);

    const isReportAvailable = isAvailable(data);

    const itemIcon = useMemo(() => {
        let logoAppId: AppId | undefined;
        switch (name) {
            case ReportsNameEnum.BrandFingerprints:
                logoAppId = AppId.BrandPerformance;
                break
            case ReportsNameEnum.Firefly:
                logoAppId = AppId.Operators;
                break
            case ReportsNameEnum.Menu:
                logoAppId = AppId.MenuTrends;
                break
            case ReportsNameEnum.Profile:
                logoAppId = AppId.Snap;
                break
            case ReportsNameEnum.LTOActivity:
                logoAppId = AppId.LaunchesAndRatings;
                break;
            default:
                break;
        }

        if (logoAppId) {
            return (
                <ProductLogo
                    appId={logoAppId}
                    type={isReportAvailable ? 'regular' : 'gs'}
                    style={{
                        width: 50,
                        height: 50,
                    }}
                />
            );
        }
    }, [name, isReportAvailable]);

    const itemPriceLabel = useMemo(() => {
        let result = '';
        switch (permissionLevel) {
            case PermissionLevelEnum.Limited: {
                result = 'limited';
                break;
            }
            case PermissionLevelEnum.None: {
                result = 'subscription required'
                break;
            }
            default:
                break;
        }
        return result;
    }, [permissionLevel]);

    const classNames = cn(
        "ReportsItem",
        !isReportAvailable && "is-disabled",
        isSelected && "is-selected",
    );

    return (
        <div
            className={classNames}
            onClick={() => onClick(name)}
        >
            {isSelected && (
                <div className="ReportsItem__status-icon">
                    <IconCheckboxFake/>
                </div>
            )}
            {!isReportAvailable && (
                <div className="ReportsItem__status-icon">
                    <IconLock/>
                </div>
            )}
            <div className="ReportsItem__content">
                <div className="ReportsItem__icon">{itemIcon}</div>
                <div className="ReportsItem__title">
                    {name}
                    <div className="ReportsItem__title-price">
                        {!!itemPriceLabel && <>({itemPriceLabel})</>}
                    </div>
                </div>
                <div className="ReportsItem__description">{description}</div>
            </div>
        </div>
    );
};

export default ReportsItem;
